import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    IWasteMovementDetail,
    IWasteMovementDwtCorrectRequest,
    IWasteMovementDwtCorrectResponse,
    IWasteMovementDwtCorrection,
    IWasteMovementDwtReadiness,
    IWasteMovementDwtRefreshResponse,
    IWasteMovementDwtSubmission,
    IWasteMovementDwtSubmitResponse,
    WasteMovementCreatePayload,
    WasteMovementUpdatePayload,
} from "@/customTypings/wasteMovement/WasteMovement";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";

function siteWasteMovementsBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/wastemovements`;
}

export const wasteMovementsService = {
    getById,
    create,
    update,
    disable,
    enable,
    softDelete,
    restore,
    getDwtReadiness,
    getDwtSubmission,
    submitDwt,
    refreshDwtStatus,
    getDwtCorrection,
    correctDwt,
};

/** S2B: the Froom answer to a submit request, with its HTTP status (200 = an attempt was made; see the API docs). */
export type DwtSubmitOutcome = { httpStatus: number; body: IWasteMovementDwtSubmitResponse };

/** S2C: the Froom answer to a refresh request, with its HTTP status (200 = the provider was asked; see the API docs). */
export type DwtRefreshOutcome = { httpStatus: number; body: IWasteMovementDwtRefreshResponse };

/** S2D: the Froom answer to a correction request, with its HTTP status (200 = the correction was sent; see the API docs). */
export type DwtCorrectOutcome = { httpStatus: number; body: IWasteMovementDwtCorrectResponse };

/** Statuses whose body is Froom's own submit answer; everything else goes through the shared response handling. */
const DWT_SUBMIT_ANSWER_STATUSES = new Set([200, 409, 422, 500, 503]);

/** Statuses whose body is Froom's own refresh answer. */
const DWT_REFRESH_ANSWER_STATUSES = new Set([200, 409, 500, 503]);

/** Statuses whose body is Froom's own correction answer (400: the reason or approver was not accepted; nothing was sent). */
const DWT_CORRECT_ANSWER_STATUSES = new Set([200, 400, 409, 422, 500, 503]);

/**
 * Froom's own DWT answers resolve with their HTTP status so the panel can show the right state; sign-in, permission and
 * not-found answers go through the shared handling (and reject).
 */
function readDwtAnswer<T>(response: Response, froomStatuses: Set<number>): Promise<{ httpStatus: number; body: T }> {
    if (!froomStatuses.has(response.status)) {
        return Promise.resolve(handleResponse(response) as Promise<unknown>).then((data) => ({
            httpStatus: response.status,
            body: (data && typeof data === "object" ? data : {}) as T,
        }));
    }
    return response.text().then((text) => {
        let body: unknown = null;
        try {
            body = text ? JSON.parse(text) : null;
        } catch {
            body = null;
        }
        return { httpStatus: response.status, body: (body && typeof body === "object" ? body : {}) as T };
    });
}

function getById(siteId: string, id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`${siteWasteMovementsBase(siteId)}/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IWasteMovementDetail>;
}

/** S2A: DEFRA DWT submission readiness of a saved movement (read-only; nothing is sent to DEFRA). */
function getDwtReadiness(siteId: string, id: string) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/${encodeURIComponent(id)}/dwt/readiness`, requestOptions).then(
        handleResponse
    ) as Promise<IWasteMovementDwtReadiness>;
}

/** S2B: the movement's DWT submission state: configured mode, reference, status, WTID, issues, whether it may be submitted. */
function getDwtSubmission(siteId: string, id: string) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/${encodeURIComponent(id)}/dwt/submission`, requestOptions).then(
        handleResponse
    ) as Promise<IWasteMovementDwtSubmission>;
}

/**
 * S2B: asks the server to submit the saved movement to DEFRA DWT in the server-configured mode. No body is sent: what is
 * submitted, where and in which mode are decided on the server. Froom's own answers (200, 409, 422, 500, 503) resolve
 * with their status so the panel can show the right state; sign-in, permission and not-found answers go through the
 * shared handling (and reject).
 */
function submitDwt(siteId: string, id: string): Promise<DwtSubmitOutcome> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/${encodeURIComponent(id)}/dwt/submit`, requestOptions).then((response) =>
        readDwtAnswer<IWasteMovementDwtSubmitResponse>(response, DWT_SUBMIT_ANSWER_STATUSES)
    );
}

/**
 * S2C: asks the server to check the movement's unresolved DWT submission with the provider ("Refresh DWT status"). No body
 * is sent: which record is checked - the persisted reference, in the unresolved submission's own mode - is decided on the
 * server, and nothing is ever submitted. Froom's own answers (200, 409, 500, 503) resolve with their status.
 */
function refreshDwtStatus(siteId: string, id: string): Promise<DwtRefreshOutcome> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/${encodeURIComponent(id)}/dwt/refresh`, requestOptions).then((response) =>
        readDwtAnswer<IWasteMovementDwtRefreshResponse>(response, DWT_REFRESH_ANSWER_STATUSES)
    );
}

/**
 * S2D: what correcting the saved movement's accepted DEFRA filing would involve now - the filing, the changed fields,
 * whether a second person must approve and who may. Read-only: nothing is sent to DEFRA.
 */
function getDwtCorrection(siteId: string, id: string) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/${encodeURIComponent(id)}/dwt/correction`, requestOptions).then(
        handleResponse
    ) as Promise<IWasteMovementDwtCorrection>;
}

/**
 * S2D: asks the server to correct the movement's accepted DEFRA filing with the saved movement. Only the reason and, when a
 * second person must approve, the approving user's id are sent: the reference, the filing's mode, the movement and who is
 * recorded as correcting it are decided on the server. Froom's own answers (200, 400, 409, 422, 500, 503) resolve with
 * their status.
 */
function correctDwt(siteId: string, id: string, request: IWasteMovementDwtCorrectRequest): Promise<DwtCorrectOutcome> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify({ reason: request.reason, approverIdentityId: request.approverIdentityId ?? null }),
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/${encodeURIComponent(id)}/dwt/correct`, requestOptions).then((response) =>
        readDwtAnswer<IWasteMovementDwtCorrectResponse>(response, DWT_CORRECT_ANSWER_STATUSES)
    );
}

function create(siteId: string, payload: WasteMovementCreatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/create`, requestOptions).then(
        handleResponse
    ) as Promise<IWasteMovementDetail>;
}

function update(siteId: string, payload: WasteMovementUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IWasteMovementDetail>>;
}

function disable(siteId: string, id: string) {
    return postQuery(siteId, "disable", id);
}

function enable(siteId: string, id: string) {
    return postQuery(siteId, "enable", id);
}

function softDelete(siteId: string, id: string) {
    return postQuery(siteId, "softdelete", id);
}

function restore(siteId: string, id: string) {
    return postQuery(siteId, "restore", id);
}

function postQuery(siteId: string, action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteWasteMovementsBase(siteId)}/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(
        handleResponse
    );
}
